import React, { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Database, Info, Lock, Monitor, Save, Settings as SettingsIcon } from "lucide-react";
import { AdminTopbar } from "../../layouts/AdminLayout";
import { Button, Loading, Select, Toggle } from "../../components/ui";
import { apiPut, useApi } from "../../api/client";

/*
 * System settings.
 *
 * Every control here is stored and read back. The page used to be a mock-up —
 * nothing loaded, the Save buttons did nothing, and it named a PostgreSQL
 * database the portal has never used — so a setting changed here was silently
 * discarded.
 *
 * The one that changes behaviour today is the results lock: how long a club may
 * amend its own results after an upload, which the server enforces on every
 * write. The rest are recorded preferences.
 */
type Settings = Record<string, any>;

const SCORING_TOGGLES: { key: string; label: string; hint: string }[] = [
  {
    key: "allowSighters",
    label: "Allow sighters (practice shots)",
    hint: "Sighters are recorded and drawn on the target, but never counted towards a score.",
  },
  {
    key: "requireSighters",
    label: "Require sighters for matches",
    hint: "Reports a string that arrives with no sighters, as a check on the upload.",
  },
  { key: "grandAggregate", label: "Enable grand match calculation", hint: "Produces the grand aggregate across every match." },
  { key: "allowScoreEditing", label: "Allow score editing after a match", hint: "Separate from the results lock above." },
  { key: "autoRankings", label: "Calculate rankings automatically", hint: "Recalculates rankings after each import." },
];

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[12px] text-muted">{hint}</p>}
    </div>
  );
}

export default function SystemSettings() {
  const { data, loading, reload } = useApi<Settings>("/settings", {});
  const [form, setForm] = useState<Settings>({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "ok" | "bad"; text: string } | null>(null);

  // Defaults matter: an empty database must still show what the server will do.
  useEffect(() => {
    setForm({
      systemName: "GRS Shoot Results Portal",
      organisation: "Global Range Solutions",
      timezone: "(AEST) Australia/Sydney",
      dateFormat: "DD MMM YYYY (25 May 2024)",
      country: "Australia",
      itemsPerPage: "25",
      resultsLockDays: 30,
      maxUploadMb: 50,
      allowSighters: true,
      requireSighters: true,
      grandAggregate: true,
      allowScoreEditing: false,
      autoRankings: true,
      ...data,
    });
  }, [data]);

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await apiPut<{ message: string }>("/admin/settings", form);
      setNotice({ tone: "ok", text: r.message || "Settings saved." });
      reload();
    } catch (e) {
      setNotice({ tone: "bad", text: (e as Error).message });
    }
    setBusy(false);
  };

  if (loading) return <div className="card"><Loading /></div>;
  const lockDays = Number(form.resultsLockDays) || 30;

  return (
    <div>
      <AdminTopbar
        title="System Settings"
        subtitle="Manage system configuration and preferences."
        actions={<Button onClick={save} disabled={busy}><Save size={16} /> {busy ? "Saving..." : "Save Changes"}</Button>}
      />

      {notice && (
        <div
          className={`mb-3 flex items-start gap-2.5 rounded-xl border px-4 py-2.5 text-[13px] ${
            notice.tone === "ok" ? "border-success/30 bg-success-soft text-success" : "border-danger/30 bg-danger-soft text-danger"
          }`}
        >
          {notice.tone === "ok" ? <CheckCircle2 size={16} className="mt-px shrink-0" /> : <AlertCircle size={16} className="mt-px shrink-0" />}
          <span className="min-w-0 font-semibold">{notice.text}</span>
          <button className="ml-auto shrink-0 font-bold opacity-70 hover:opacity-100" onClick={() => setNotice(null)}>✕</button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[
          { icon: SettingsIcon, cls: "bg-primary-soft text-primary", l: "System Version", v: "v1.0.0", s: "" },
          { icon: Database, cls: "bg-success-soft text-success", l: "Database", v: "MongoDB", s: "Connected", sCls: "text-success" },
          {
            icon: Monitor,
            cls: "bg-violet-50 text-violet-600",
            l: "Local Time",
            v: new Date().toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }),
            s: form.timezone,
          },
        ].map((c) => (
          <div key={c.l} className="card flex items-start gap-3 p-3.5">
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${c.cls}`}><c.icon size={18} /></span>
            <div className="min-w-0">
              <div className="text-xs text-muted">{c.l}</div>
              <div className="truncate text-[15px] font-bold leading-tight text-ink">{c.v}</div>
              {c.s && <div className={`truncate text-[11px] font-semibold ${c.sCls ?? "text-muted"}`}>{c.s}</div>}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 space-y-3">
        {/*
          The results lock. A club may correct its own results while a shoot is
          fresh; once a meeting is settled the record has to stop moving, or a
          published result could change under someone weeks later. There is no
          manual lock switch by design — the clock is the same for every club.
        */}
        <div className="card p-4">
          <h3 className="flex items-center gap-2 text-[15px] font-bold text-ink">
            <Lock size={16} className="text-primary" /> Results &amp; Access
          </h3>
          <div className="mt-3 grid gap-x-8 gap-y-4 lg:grid-cols-2">
            <Field
              label="Lock results after"
              hint={`A club can amend its own results for ${lockDays} day${lockDays === 1 ? "" : "s"} after an upload. The competition shows the date it locks.`}
            >
              <div className="flex">
                <input
                  type="number"
                  min={1}
                  className="input rounded-r-none"
                  value={form.resultsLockDays ?? 30}
                  onChange={(e) => set("resultsLockDays", e.target.value)}
                />
                <span className="flex h-10 items-center rounded-r-lg border border-l-0 border-line bg-slate-50 px-3.5 text-sm font-bold text-muted">
                  days
                </span>
              </div>
            </Field>
            <div className="flex items-start gap-2.5 self-end rounded-xl border border-primary/20 bg-primary-soft px-3.5 py-2.5 text-[12px] text-primary">
              <Info size={15} className="mt-px shrink-0" />
              <span>
                Applies to club logins only. The Association can always amend a result, and the lock is enforced by the
                server rather than by hiding buttons.
              </span>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <h3 className="text-[15px] font-bold text-ink">General</h3>
          <div className="mt-3 grid gap-x-8 gap-y-4 lg:grid-cols-3">
            <Field label="System Name">
              <input className="input" value={form.systemName ?? ""} onChange={(e) => set("systemName", e.target.value)} />
            </Field>
            <Field label="Organisation Name">
              <input className="input" value={form.organisation ?? ""} onChange={(e) => set("organisation", e.target.value)} />
            </Field>
            <Field label="System Timezone">
              <Select
                options={["(AEST) Australia/Sydney", "(AEST) Australia/Brisbane", "(ACST) Australia/Adelaide", "(AWST) Australia/Perth"]}
                value={form.timezone}
                onChange={(v) => set("timezone", v)}
              />
            </Field>
            <Field label="Date Format">
              <Select
                options={["DD MMM YYYY (25 May 2024)", "DD/MM/YYYY", "MM/DD/YYYY"]}
                value={form.dateFormat}
                onChange={(v) => set("dateFormat", v)}
              />
            </Field>
            <Field label="Default Country">
              <Select options={["Australia", "New Zealand", "United Kingdom"]} value={form.country} onChange={(v) => set("country", v)} />
            </Field>
            <Field label="Rows per page" hint="Admin tables only — public result tables always show the full field.">
              <Select options={["25", "10", "50", "100"]} value={String(form.itemsPerPage ?? "25")} onChange={(v) => set("itemsPerPage", v)} />
            </Field>
          </div>
        </div>

        <div className="card p-4">
          <h3 className="text-[15px] font-bold text-ink">Scoring &amp; Match Rules</h3>
          <div className="mt-3 max-w-3xl divide-y divide-line">
            {SCORING_TOGGLES.map((t) => (
              <div key={t.key} className="flex items-start justify-between gap-6 py-2.5 first:pt-0 last:pb-0">
                <span className="text-[13px] text-slate-700">
                  <span className="font-semibold">{t.label}</span>
                  <span className="mt-0.5 block text-[12px] text-muted">{t.hint}</span>
                </span>
                <Toggle on={!!form[t.key]} onChange={(v) => set(t.key, v)} />
              </div>
            ))}
          </div>
        </div>

        <div className="card p-4">
          <h3 className="text-[15px] font-bold text-ink">Data &amp; Import</h3>
          <div className="mt-3 grid gap-x-8 gap-y-4 lg:grid-cols-3">
            <Field label="Maximum upload file size" hint="Allowed: 1 – 200 MB.">
              <div className="flex">
                <input
                  type="number"
                  min={1}
                  max={200}
                  className="input rounded-r-none"
                  value={form.maxUploadMb ?? 50}
                  onChange={(e) => set("maxUploadMb", e.target.value)}
                />
                <span className="flex h-10 items-center rounded-r-lg border border-l-0 border-line bg-slate-50 px-3.5 text-sm font-bold text-muted">
                  MB
                </span>
              </div>
            </Field>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={busy}><Save size={16} /> {busy ? "Saving..." : "Save Changes"}</Button>
        </div>
      </div>
    </div>
  );
}
